import { effect } from "@preact/signals";
import $ from "jquery";
import * as Preact from "preact";
import * as _ from "underscore";

import * as cjkFontLoader from "cjkFontLoader";
import loadHandwritingFont from "loadHandwritingFont";
import * as readerColorScheme from "readerColorScheme";
import * as readerReviewUtils from "readerReviewUtils";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import stores from "stores/stores";
import type { ReaderSettings } from "stores/UserStore";
import * as themeColor from "themeColor";
import { schedulePopoverReposition } from "views/readerText/ReaderPopoverLayer";
import * as ttsUi from "views/ttsUi";
import TTSVoiceSettingsView from "views/TTSVoiceSettingsView";
import * as browserTTSVoices from "../browserTTSVoices";

const ranges = {
  fontSize: {
    min: 0,
    max: 8,
    initial: 5,
  },
  width: {
    min: 1,
    max: 4,
    initial: 3,
  },
  lineHeight: {
    min: 0,
    max: 4,
    initial: 2,
  },
};

const getNumber = (name) => {
  const body = $("body");
  for (let number = 0; number < 9; number++) {
    if (body.hasClass(name + number)) {
      return number;
    }
  }
  return ranges[name].initial;
};

export interface FormattingOptionsProps {
  transliteration?: string;
  showReaderReviewOption: boolean;
  restartTTSIfPlayingOrPlayDemo: () => void;
  audioFileMode?: boolean;
  bookLanguage?: string;
  readAloudPlaybackActive?: boolean;
}

/** Map of font name to the value of the `font-family` CSS attribute */
const fontToStyle = {
  Helvetica: "Helvetica, Arial, sans-serif",
  "Open Sans": "'Open Sans', sans-serif",
  "Times New Roman": "'Times New Roman', serif",
  Jost: "Jost, sans-serif",
};

class FormattingOptions extends Preact.Component<FormattingOptionsProps> {
  private disposePageSettingsEffect?: () => void;

  /** A JQuery element corresponding to the HTML body. */
  body: JQuery;

  capitalizeFirstLetter(input) {
    return input.charAt(0).toUpperCase() + input.slice(1);
  }

  /**
   * Apply the settings to the page.
   *
   * It's hacky to do all this in this view module. Consider moving elsewhere.
   */
  applySettingsToPage(settings = stores.user.readerSettings.value) {
    // Font
    const bookLanguage = this.props.bookLanguage;
    const cjkRegion = bookLanguage
      ? languageMap.getCJKFontRegion(bookLanguage)
      : undefined;

    if (cjkRegion) {
      // CJK font handling
      const cjkFont = (settings.readerFontCJK ||
        "Noto Sans") as cjkFontLoader.CJKFontFamily;
      cjkFontLoader.loadFontsForRegion(cjkFont, cjkRegion).then(() => {
        this.body
          .find("#bookContent")
          .css(
            "font-family",
            cjkFontLoader.getCSSFontFamily(cjkFont, cjkRegion)
          );
      });
      // Also apply immediately in case fonts are already loaded
      this.body
        .find("#bookContent")
        .css("font-family", cjkFontLoader.getCSSFontFamily(cjkFont, cjkRegion));
    } else {
      // Standard Latin font handling
      this.body
        .find("#bookContent")
        .css(
          "font-family",
          fontToStyle[settings.readerFont] ?? settings.readerFont
        );
    }

    // Translation style
    const translationStyle = settings.readerTranslationStyle;
    this.body.removeClass("glossStyleNew");
    this.body.removeClass("glossStyle");
    this.body.removeClass("popoverStyle");

    // Every style, not just gloss/popover: the Smart Dictionary and Words tab
    // show translations in the handwriting font too.
    loadHandwritingFont();
    if (translationStyle === "gloss") {
      this.body.addClass("glossStyle");
    } else if (translationStyle === "glossNew") {
      this.body.addClass("glossStyleNew");
    } else if (translationStyle === "popover") {
      this.body.addClass("popoverStyle");
    }
    schedulePopoverReposition();

    // Translation font
    if (settings.readerTranslationFont === "normal") {
      this.body.addClass("normalTranslationFont");
    } else {
      this.body.removeClass("normalTranslationFont");
    }

    // Translations
    if (settings.readerTranslations === "off") {
      this.body.addClass("translationsOff");
    } else {
      this.body.removeClass("translationsOff");
    }

    // Alignment
    this.setBodyClass(settings.readerAlign, ["alignLeft", "alignJustify"]);

    // Update color scheme
    readerColorScheme.applyToBody();
    themeColor.update();
  }

  setBodyClass(toAdd, possibleValues) {
    for (const className of _.without(possibleValues, toAdd)) {
      this.body.removeClass(className);
    }
    this.body.addClass(toAdd);
  }

  setNumber(name, number) {
    this.body.removeClass(name + getNumber(name));
    this.body.addClass(name + number);
    localStorage.setItem("reader." + name, number);
    schedulePopoverReposition();
  }

  increaseNumber(name) {
    let value = getNumber(name);
    if (value < ranges[name].max) {
      value++;
      this.setNumber(name, value);
    }
  }

  decreaseNumber(name) {
    let value = getNumber(name);
    if (value > ranges[name].min) {
      value--;
      this.setNumber(name, value);
    }
  }

  constructor(props: FormattingOptionsProps) {
    super(props);

    this.body = $("body");

    this.setNumber(
      "fontSize",
      localStorage.getItem("reader.fontSize") || ranges.fontSize.initial
    );
    this.setNumber(
      "lineHeight",
      localStorage.getItem("reader.lineHeight") || ranges.lineHeight.initial
    );
    this.setNumber(
      "width",
      localStorage.getItem("reader.width") || ranges.width.initial
    );
  }

  componentDidMount() {
    this.disposePageSettingsEffect = effect(() => {
      this.applySettingsToPage(stores.user.readerSettings.value);
    });
  }

  componentWillUnmount() {
    this.body.removeClass("alignLeft");
    this.body.removeClass("alignJustify");

    this.body.removeClass("glossStyle");
    this.body.removeClass("glossStyleNew");
    this.body.removeClass("popoverStyle");
    this.body.removeClass("normalTranslationFont");
    this.disposePageSettingsEffect?.();
  }

  selectColorScheme = (colorScheme: string) => {
    readerColorScheme.set(colorScheme);
    this.applySettingsToPage();
    this.forceUpdate();
  };

  ttsRate = () => {
    return browserTTSVoices.getRate(stores.bookModel.book.value.language);
  };

  render() {
    const { transliteration, showReaderReviewOption, bookLanguage } =
      this.props;
    const user = stores.user;
    const readerSettings = user.readerSettings.value;

    const cjkRegion = bookLanguage
      ? languageMap.getCJKFontRegion(bookLanguage)
      : undefined;

    return (
      <div id="formattingOptions">
        <h3>Size</h3>
        <p>
          <span
            class="clickable"
            onClick={() => this.decreaseNumber("fontSize")}
          >
            - Font
          </span>
          <span
            class="clickable"
            onClick={() => this.increaseNumber("fontSize")}
          >
            + Font
          </span>
        </p>
        <p>
          <span
            class="clickable"
            onClick={() => this.decreaseNumber("lineHeight")}
          >
            - Line Height
          </span>
          <span
            class="clickable"
            onClick={() => this.increaseNumber("lineHeight")}
          >
            + Line Height
          </span>
        </p>
        <p class="noMobile">
          <span class="clickable" onClick={() => this.decreaseNumber("width")}>
            <i class="icon-arrow-right"></i> Column{" "}
            <i class="icon-arrow-left"></i>
          </span>
          <span class="clickable" onClick={() => this.increaseNumber("width")}>
            <i class="icon-arrow-left"></i> Column{" "}
            <i class="icon-arrow-right"></i>
          </span>
        </p>

        <h3>Appearance</h3>
        <p>
          {cjkRegion
            ? cjkFontLoader.cjkFontOptions.map((font) => (
                <span
                  class={cn({
                    clickable: true,
                    fontOption: true,
                    selected:
                      (readerSettings.readerFontCJK || "Noto Sans") === font,
                  })}
                  style={
                    font === "Handwriting"
                      ? 'font-family: "Delius", cursive'
                      : `font-family: ${cjkFontLoader.getCSSFontFamily(font, cjkRegion)}`
                  }
                  onClick={() => {
                    cjkFontLoader.loadFontsForRegion(font, cjkRegion);
                    user.save({ readerFontCJK: font });
                  }}
                >
                  {font}
                </span>
              ))
            : ["Helvetica", "Open Sans", "Times New Roman", "Jost"].map(
                (font) => (
                  <span
                    class={cn({
                      clickable: true,
                      fontOption: true,
                      selected: readerSettings.readerFont === font,
                    })}
                    style={fontToStyle[font]}
                    onClick={() => user.save({ readerFont: font })}
                  >
                    {font}
                  </span>
                )
              )}
        </p>
        <p>
          {[
            { displayName: "E-Ink", color: "Light" },
            { displayName: "Light", color: "Sepia" },
            { displayName: "Dark", color: "Dark" },
          ].map(({ displayName, color }) => (
            <span
              class={cn({
                clickable: true,
                selected: readerColorScheme.get() === `color${color}`,
              })}
              onClick={() => {
                this.selectColorScheme(`color${color}`);
              }}
            >
              {displayName}
            </span>
          ))}
        </p>
        <UserFieldValueSelector
          fieldName="readerAlign"
          possibleValues={[
            {
              displayName: (
                <>
                  <i class="icon-align-left"></i> &nbsp; Align Left
                </>
              ),
              value: "alignLeft",
            },
            {
              displayName: (
                <>
                  <i class="icon-align-justify"></i> &nbsp; Justify
                </>
              ),
              value: "alignJustify",
            },
          ]}
        />
        <UserFieldValueSelector
          fieldName="readerSentenceBreaks"
          possibleValues={[
            {
              displayName: <>No Sentence Breaks</>,
              value: "off",
            },
            {
              displayName: <>Sentence Breaks</>,
              value: "on",
            },
          ]}
        />

        <h3>Translations</h3>
        <UserFieldValueSelector
          fieldName="readerTranslations"
          possibleValues={[
            {
              displayName: <>Translations On</>,
              value: "on",
            },
            {
              displayName: (
                <>
                  <span class="noMobile">Translations </span>Off{" "}
                  <small>(monolingual)</small>
                </>
              ),
              value: "off",
            },
          ]}
          style="fitContent"
        />
        {readerSettings.readerTranslations === "off" && (
          <UserFieldValueSelector
            fieldName="readerMonolingualAutoOpen"
            possibleValues={[
              {
                displayName: <>Auto-open Sidebar</>,
                value: "on",
              },
              {
                displayName: <>Don't Auto-open Sidebar</>,
                value: "off",
              },
            ]}
            style="fitContent"
          />
        )}
        {transliteration && (
          <p>
            <span
              class={cn({
                clickable: true,
                selected: user.hasFeature("showTransliterations"),
              })}
              style="width: fit-content"
              onClick={() => user.setFeature("showTransliterations", true)}
            >
              Show {transliteration}
            </span>
            <span
              class={cn({
                clickable: true,
                selected: !user.hasFeature("showTransliterations"),
              })}
              style="width: fit-content"
              onClick={() => user.setFeature("showTransliterations", false)}
            >
              Don't Show {transliteration}
            </span>
          </p>
        )}

        {user.hasPremiumAccess.value && (
          <p>
            <span
              class={cn({
                clickable: true,
                selected: !user.hasFeature("contextTranslations"),
              })}
              onClick={() => user.setFeature("contextTranslations", false)}
            >
              Basic Translations
            </span>
            <span
              class={cn({
                clickable: true,
                selected: user.hasFeature("contextTranslations"),
              })}
              onClick={() => user.setFeature("contextTranslations", true)}
            >
              Context Aware <small>(better)</small>
            </span>
          </p>
        )}
        <UserFieldValueSelector
          fieldName="readerTranslationStyle"
          possibleValues={[
            {
              displayName: <>Highlight</>,
              value: "glossNew",
            },
            {
              displayName: <>Underline</>,
              value: "gloss",
            },
            {
              displayName: <>Replace</>,
              value: "replace",
            },
            {
              displayName: <>Popover</>,
              value: "popover",
            },
          ]}
        />
        <UserFieldValueSelector
          fieldName="readerMergePhrases"
          possibleValues={[
            {
              displayName: <>Don't Merge</>,
              value: "off",
              title:
                "Adjacent words will be translated separately. Drag across words manually to translate phrases.",
            },
            {
              displayName: <>Merge Phrases</>,
              value: "on",
              title:
                "Selecting two adjacent words will combine them into a phrase.",
            },
          ]}
        />
        <UserFieldValueSelector
          fieldName="readerTranslationFont"
          possibleValues={[
            {
              displayName: <>Cursive Font</>,
              value: "cursive",
            },
            {
              displayName: <>Normal Font</>,
              value: "normal",
            },
          ]}
        />

        <h3>On deselecting a word or phrase</h3>
        <UserFieldValueSelector
          fieldName="readerDeselectBehavior"
          possibleValues={[
            {
              displayName: <>Delete Flashcard</>,
              value: "delete",
            },
            {
              displayName: <>Keep Flashcard</>,
              value: "keep",
            },
          ]}
        />

        {showReaderReviewOption && (
          <>
            <h3>Review summary buttons</h3>
            <UserFieldValueSelector
              fieldName="readerReview"
              selectedValue={readerReviewUtils.setting(stores)}
              possibleValues={[
                {
                  displayName: <>Show</>,
                  value: "show",
                },
                {
                  displayName: <>Hide</>,
                  value: "hide",
                },
              ]}
            />
          </>
        )}

        <div class="autoHighlightOptions">
          <h3>Auto-highlight words</h3>
          <UserFieldValueSelector
            fieldName="readerWordHighlights"
            possibleValues={[
              {
                displayName: <>None</>,
                value: "off",
              },
              {
                displayName: <>Ready To Learn</>,
                value: "readyToLearn",
              },
              {
                displayName: <>All Your Words</>,
                value: "on",
              },
            ]}
            style="fitContent"
          />
        </div>

        <h3 class="streakOptions">Show streak in the top bar</h3>
        <UserFieldValueSelector
          fieldName="readerStreakShow"
          possibleValues={[
            {
              displayName: <>Always Show</>,
              value: "always",
            },
            {
              displayName: <>Show Until Complete</>,
              value: "untilComplete",
            },
            {
              displayName: <>Never Show</>,
              value: "never",
            },
          ]}
          style="column"
        />

        {bookLanguage && !this.props.audioFileMode ? (
          <TTSVoiceSettingsView
            language={bookLanguage}
            readAloudPlaybackActive={this.props.readAloudPlaybackActive}
            onReadAloudSettingsChanged={
              this.props.restartTTSIfPlayingOrPlayDemo
            }
          />
        ) : null}

        {this.props.audioFileMode ? (
          <div class="readAloudRateSetting">
            <h3 class="readAloudRateHeading">Audio speed</h3>
            <p class="readAloudRate">
              {ttsUi.RATE_CHOICES.map((rate) => (
                <span
                  class={cn({
                    clickable: true,
                    readAloudRateOption: true,
                    selected: rate.toString() === this.ttsRate(),
                  })}
                  data-value={rate}
                  onClick={() => {
                    browserTTSVoices.setRate(
                      stores.bookModel.book.value.language,
                      rate.toString()
                    );
                    this.forceUpdate();
                    this.props.restartTTSIfPlayingOrPlayDemo?.();
                  }}
                >
                  {rate}X
                </span>
              ))}
            </p>
          </div>
        ) : null}

        <p style="margin-top: 24px;">
          <small>
            <a
              target="_blank"
              href="https://forum.readlang.com/t/guide-to-the-options-in-the-reader-page-aa-tab/1294"
            >
              Learn about all the above options
            </a>
          </small>
        </p>
      </div>
    );
  }
}

const UserFieldValueSelector: Preact.FunctionalComponent<{
  fieldName: keyof ReaderSettings;
  possibleValues: {
    value: string;
    displayName: string | Preact.JSX.Element;
    title?: string;
  }[];
  style?: "fitContent" | "column";
  /** Overrides the stored value, e.g. to show the default when it's unset */
  selectedValue?: string;
}> = ({ fieldName, possibleValues, style, selectedValue }) => {
  const user = stores.user;
  const selected = selectedValue ?? user.readerSettings.value[fieldName];
  return (
    <p class={cn({ column: style === "column" })}>
      {possibleValues.map(({ value, displayName, title }) => (
        <span
          class={cn({
            clickable: true,
            selected: selected === value,
          })}
          onClick={() => user.save({ [fieldName]: value })}
          style={style === "fitContent" ? "width: fit-content" : ""}
          title={title}
        >
          {displayName}
        </span>
      ))}
    </p>
  );
};

export default FormattingOptions;
